import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import Button from "../../components/Core/Button/Button";
import { AppSelectController } from "../../components/Core/AppSelect/AppSelect";
import { FormCard } from "../../components/Core/FormCard";
import TextAreaNew from "../../components/Core/TextArea/TextAreaNew";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import useUnsavedChangesInFormChecker from "../../hooks/useUnsavedChangesChecker";
import Customer, { UpdateCustomerRequest } from "../../models/Customer";
import { Pool } from "../../models/Organisation";
import { useUpdateCustomer } from "../../networking/customer.hooks";
import { useCustomFieldDefinitions, useOrganisation } from "../../networking/organisation.hooks";
import {
	CustomFieldFormValues,
	CustomFieldsSection,
	buildCustomFieldsPayloadWithStored,
	customFieldFormValues,
	customFieldsSchema,
	filterCustomFieldsByPlacements,
} from "../../components/Shared/CustomFields";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import {
	useAvailablePools,
	ORGANISATION_DIRECTLY_VALUE,
} from "../../components/Shared/SubsidiarySelect/SubsidiarySelect";
import { useMediaStore } from "../../stores/media.store";

// Still an observer: the profile picture is read from mediaStore (the upload
// flow writes it there before the customer is saved).
const CustomerProfileTab: React.FC<{ customer: Customer }> = ({ customer }) => {
	const mediaStore = useMediaStore();
	const { t } = useTranslation();

	const { mutate: updateCustomer } = useUpdateCustomer();
	const { data: organisation } = useOrganisation();
	const { subsidiaries } = useSubsidiaries();

	const { profilePicture } = mediaStore;

	// Customer type / corporate-group membership is managed on the invoice tab,
	// since it governs which entity is invoiced. Here we only surface the group
	// name (read-only) when the customer belongs to one.
	const corporateGroupName = customer?.corporateGroup?.name;

	// Org-defined customer fields on this tab: placement "profil" (absent)
	// renders inside the Grunduppgifter card, "ovrigt" in its own "Övriga
	// uppgifter" card below. The faktura placements live on the
	// Fakturainformation tab. This form had no resolver before — the schema
	// below validates the custom fields only.
	const allCustomerFieldDefinitions = useCustomFieldDefinitions("customer");
	const customFieldDefinitions = useMemo(
		() => filterCustomFieldsByPlacements(allCustomerFieldDefinitions, ["profil", "ovrigt"]),
		[allCustomerFieldDefinitions]
	);
	// A koncern member gets the koncern's value for group-owned fields
	// (merged into `customer.customFields` by the backend) — rendered
	// read-only, never required, never sent.
	const corporateGroupMember = !!customer?.corporateGroup?.id;
	const validationSchema = useMemo(
		() =>
			yup.object().shape({
				customFields: customFieldsSchema(customFieldDefinitions, undefined, {
					corporateGroupMember,
				}),
			}),
		[customFieldDefinitions, corporateGroupMember]
	);

	const { register, handleSubmit, reset, control, formState } = useForm<
		Omit<Customer, "customFields"> & { customFields?: CustomFieldFormValues }
	>({
		defaultValues: useMemo(() => {
			const customFields = customFieldFormValues(customFieldDefinitions, customer?.customFields);
			if (organisation?.pools?.length)
				return {
					...customer,
					pool: customer?.pool || "",
					customFields,
				};
			else return { ...customer, customFields };
		}, [customer, organisation, customFieldDefinitions]),
		resolver: yupResolver(validationSchema),
	});

	const availablePools = useAvailablePools(
		control,
		"subsidiaryId",
		subsidiaries,
		organisation?.pools,
		customer?.pool
	);

	// "Inget dotterbolag" uses a sentinel value (Radix forbids an empty
	// SelectItem value); on submit it maps to a null subsidiaryId, clearing any
	// existing link.
	const subsidiaryOptions: { key: string; name: string }[] = [
		{ key: ORGANISATION_DIRECTLY_VALUE, name: t("NO_SUBSIDIARY") },
		...subsidiaries.map((s) => ({ key: s.id, name: s.name })),
	];

	const onSubmit = handleSubmit(async (e) => {
		// Tags are deliberately NOT sent — they are managed via the tags submenu
		// in the detail page's "..." menu, not by this form.
		const updateCustomerProfile: UpdateCustomerRequest = {
			name: e.name,
			companyImage: profilePicture,
			phoneNumber: e.phoneNumber,
			vatNumber: e.vatNumber,
			internalNotes: e.internalNotes,
			description: e.description,
			pool: e.pool || undefined,
			subsidiaryId:
				e.subsidiaryId && e.subsidiaryId !== ORGANISATION_DIRECTLY_VALUE ? e.subsidiaryId : null,
			visitingAddress: {
				address1: e.visitingAddress?.address1,
				postalCode: e.visitingAddress?.postalCode,
				city: e.visitingAddress?.city,
			},
			// Every active customer key: this tab's from the form, the
			// Fakturainformation tab's from the stored customer (so a Profil save
			// does not clear them). Archived keys are kept by the backend.
			customFields: buildCustomFieldsPayloadWithStored(
				e.customFields,
				customFieldDefinitions,
				allCustomerFieldDefinitions,
				customer?.customFields,
				{ corporateGroupMember }
			),
		};

		if (customer?.id) {
			// The hook toasts success/error and refetches the customer, which flows
			// back down as a new `customer` prop and resets the form.
			updateCustomer({ customerId: customer.id, update: updateCustomerProfile });
		}
	});

	useUnsavedChangesInFormChecker(formState);

	useEffect(() => {
		reset({
			...customer,
			customFields: customFieldFormValues(customFieldDefinitions, customer?.customFields),
		});
	}, [customer, reset, customFieldDefinitions]);

	return (
		<div>
			<form onSubmit={onSubmit}>
				<div className="max-w-[1200px] pr-4 py-6 xl:max-w-[2100px]">
					<div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
						<div className="flex flex-col gap-5">
							<FormCard title="BASIC_INFO">
								{corporateGroupName && (
									<div className="mb-4">
										<AppInputNew
											label="corporateGroup.title"
											value={corporateGroupName}
											disabled
											readOnly
										/>
									</div>
								)}
								<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
									{/* Full width — customer names are long, and it lets the two
									    remaining detail fields pair up on the next row. */}
									<AppInputNew
										{...register("name")}
										containerClassName="md:col-span-2"
										label="customer.name"
										placeholder=""
										mandatory
									/>
									<AppInputNew
										{...register("phoneNumber")}
										label="PHONE"
										placeholder={t("PHONE_PLACEHOLDER")}
										icon="phone"
									/>
									<AppInputNew
										{...register("vatNumber")}
										label="ORG_OR_PERSONAL_NUMBER"
										placeholder="ORG_OR_PERSONAL_NUMBER"
									/>
									{/* Organisation placement and bookkeeping, kept paired on
									    their own rows at the bottom of the card. */}
									<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:col-span-2 md:grid-cols-2">
										{availablePools.length > 0 && (
											<AppSelectController<Pool>
												control={control}
												label="POOL"
												name="pool"
												options={availablePools}
												getOptionLabel={(option) => option.name}
												getOptionValue={(o) => o.key as string}
												errors={formState.errors?.pool?.message}
												placeholder="SELECT_POOL"
												margin="0"
												mandatory
											/>
										)}
										{subsidiaries.length > 0 && (
											<AppSelectController<{ key: string; name: string }>
												control={control}
												label="BELONGS_TO_SUBSIDIARY"
												name="subsidiaryId"
												options={subsidiaryOptions}
												getOptionLabel={(o) => o.name}
												getOptionValue={(o) => o.key}
												placeholder="NO_SUBSIDIARY"
												margin="0"
											/>
										)}
									</div>
								</div>
								{/* Customer fields placed under Profil — hidden when there are none. */}
								<CustomFieldsSection
									entity="customer"
									placement="profil"
									variant="inline"
									definitions={allCustomerFieldDefinitions}
									control={control}
									register={register}
									errors={formState.errors}
									corporateGroupMember={corporateGroupMember}
									corporateGroupValues={customer?.customFields ?? {}}
								/>
							</FormCard>

							<FormCard title="VISITING_ADDRESS">
								<div className="flex flex-col gap-4">
									<AppInputNew
										{...register("visitingAddress.address1")}
										label="STREET_ADDRESS"
										placeholder={t("ADDRESS_PLACEHOLDER")}
									/>
									<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
										<AppInputNew
											{...register("visitingAddress.postalCode")}
											label="POSTAL_CODE"
											placeholder="POSTAL_CODE_PLACEHOLDER"
										/>
										<AppInputNew
											{...register("visitingAddress.city")}
											label="CITY"
											placeholder="CITY_PLACEHOLDER"
										/>
									</div>
								</div>
							</FormCard>
						</div>

						<div className="flex flex-col gap-5">
							<FormCard title="DESCRIPTION">
								<div className="flex flex-col gap-4">
									<TextAreaNew
										{...register("description")}
										label="ABOUT_WORKPLACE"
										height="200px"
									/>
									<TextAreaNew
										{...register("internalNotes")}
										label="customer.internalNotes"
										height="200px"
									/>
								</div>
							</FormCard>

							{/* Customer fields placed under "Övriga uppgifter" — own card, hidden when there are none. */}
							<CustomFieldsSection
								entity="customer"
								placement="ovrigt"
								definitions={allCustomerFieldDefinitions}
								control={control}
								register={register}
								errors={formState.errors}
								corporateGroupMember={corporateGroupMember}
								corporateGroupValues={customer?.customFields ?? {}}
							/>
						</div>
					</div>
				</div>

				<FloatingButtonPanel>
					<Button width="9.75rem">{t("SAVE_CHANGES")}</Button>
				</FloatingButtonPanel>
			</form>
		</div>
	);
};

export default CustomerProfileTab;
